// Crowd-sourced map contribution panel.
// Upload Damos / A2L / XDF / PRG raw definitions. Earn credits + contributor badges.
// Shown on the Dashboard for authenticated users.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  UploadCloud, Award, CheckCircle2, Sparkles, Users, FileText,
  AlertTriangle, Loader2, TrendingUp, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ACCEPT = '.a2l,.damos,.xdf,.prg,.dam,.kp';
const MAX_MB = 50;

const BADGE_STYLE = {
  CONTRIBUTOR:          { color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/40',   label: 'Contributor' },
  SILVER_CONTRIBUTOR:   { color: 'text-zinc-200',    bg: 'bg-zinc-400/15',    border: 'border-zinc-400/40',    label: 'Silver' },
  GOLD_CONTRIBUTOR:     { color: 'text-yellow-300',  bg: 'bg-yellow-500/15',  border: 'border-yellow-500/50',  label: 'Gold' },
  PLATINUM_CONTRIBUTOR: { color: 'text-cyan-200',    bg: 'bg-cyan-500/15',    border: 'border-cyan-400/50',    label: 'Platinum' },
};

function BadgeChip({ code }) {
  if (!code) return null;
  const s = BADGE_STYLE[code] || BADGE_STYLE.CONTRIBUTOR;
  return (
    <span
      data-testid="contributor-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider ${s.bg} ${s.border} ${s.color}`}
    >
      <Award className="w-3 h-3" />{s.label}
    </span>
  );
}

function UploadForm({ token, onDone }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ ecu_type: '', vehicle_make: '', vehicle_model: '', year: '', notes: '' });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { setError(`File exceeds ${MAX_MB} MB limit`); return; }
    setError('');
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !meta.ecu_type.trim()) { setError('File and ECU type are required'); return; }
    setUploading(true);
    setError('');
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('ecu_type', meta.ecu_type.trim());
      if (meta.vehicle_make) fd.append('vehicle_make', meta.vehicle_make.trim());
      if (meta.vehicle_model) fd.append('vehicle_model', meta.vehicle_model.trim());
      if (meta.year) fd.append('year', String(parseInt(meta.year, 10) || ''));
      if (meta.notes) fd.append('notes', meta.notes.trim());

      const resp = await fetch(`${API_URL}/api/contribute/definition`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json.detail || `Upload failed (${resp.status})`);
      setLastResult(json);
      setFile(null);
      setMeta({ ecu_type: '', vehicle_make: '', vehicle_model: '', year: '', notes: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      onDone?.();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contribute-form">
      {/* File drop zone */}
      <label
        htmlFor="contrib-file"
        className={`block border-2 border-dashed rounded-sm p-5 text-center cursor-pointer transition-colors ${
          file ? 'border-[#007AFF]/50 bg-[#007AFF]/5' : 'border-[#27272A] hover:border-zinc-600 bg-[#0A0A0A]'
        }`}
      >
        <input
          ref={fileInputRef}
          id="contrib-file"
          type="file"
          accept={ACCEPT}
          onChange={pick}
          className="hidden"
          data-testid="contribute-file-input"
        />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <FileText className="w-5 h-5 text-[#007AFF]" />
            <div className="text-left">
              <div className="text-sm text-white font-medium truncate max-w-xs">{file.name}</div>
              <div className="text-xs text-zinc-500 font-['JetBrains_Mono']">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              className="text-zinc-500 hover:text-white ml-2"
              data-testid="clear-file-btn"
            ><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <>
            <UploadCloud className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
            <div className="text-sm text-white font-medium">Drop Damos / A2L / XDF / PRG here</div>
            <div className="text-xs text-zinc-500 mt-1">or click to browse • max {MAX_MB} MB</div>
          </>
        )}
      </label>

      {/* Metadata */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          required
          placeholder="ECU type (e.g. Bosch MED17, MSV80) *"
          value={meta.ecu_type}
          onChange={(e) => setMeta({ ...meta, ecu_type: e.target.value })}
          data-testid="contribute-ecu-type"
          className="bg-[#0A0A0A] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none"
        />
        <input
          placeholder="Vehicle make (optional)"
          value={meta.vehicle_make}
          onChange={(e) => setMeta({ ...meta, vehicle_make: e.target.value })}
          data-testid="contribute-make"
          className="bg-[#0A0A0A] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none"
        />
        <input
          placeholder="Model (optional)"
          value={meta.vehicle_model}
          onChange={(e) => setMeta({ ...meta, vehicle_model: e.target.value })}
          data-testid="contribute-model"
          className="bg-[#0A0A0A] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none"
        />
        <input
          type="number"
          placeholder="Year (optional)"
          value={meta.year}
          onChange={(e) => setMeta({ ...meta, year: e.target.value })}
          data-testid="contribute-year"
          className="bg-[#0A0A0A] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none"
        />
      </div>
      <textarea
        placeholder="Notes (optional) — e.g. 'factory Damos pulled from WinOLS export, fuel + timing labeled'"
        value={meta.notes}
        onChange={(e) => setMeta({ ...meta, notes: e.target.value })}
        rows={2}
        data-testid="contribute-notes"
        className="w-full bg-[#0A0A0A] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none resize-none"
      />

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-red-300" data-testid="contribute-error">{error}</span>
        </div>
      )}

      {lastResult && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-sm"
          data-testid="contribute-success"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="text-emerald-300 font-bold">+{lastResult.credits_awarded} credits awarded</div>
            <div className="text-zinc-300 text-xs mt-1">{lastResult.message}</div>
            {lastResult.badge && (
              <div className="mt-2"><BadgeChip code={lastResult.badge} /></div>
            )}
          </div>
        </motion.div>
      )}

      <button
        type="submit"
        disabled={!file || !meta.ecu_type.trim() || uploading}
        data-testid="contribute-submit-btn"
        className={`w-full py-3 rounded-sm font-bold text-sm tracking-[0.12em] uppercase flex items-center justify-center gap-2 transition-all ${
          !file || !meta.ecu_type.trim() || uploading
            ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
            : 'bg-[#FF5E1A] hover:bg-[#FF7A42] text-white hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,94,26,0.35)]'
        }`}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
        {uploading ? 'Uploading...' : 'Contribute & Earn Credits'}
      </button>
    </form>
  );
}

export default function MapContributionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

  const refresh = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const resp = await fetch(`${API_URL}/api/contribute/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setData(await resp.json());
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('contribute/my failed:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!token) return null;

  return (
    <div className="bg-[#0F0F0F] border border-[#27272A] rounded-sm overflow-hidden" data-testid="map-contribution-panel">
      {/* Header */}
      <div className="relative p-6 bg-gradient-to-br from-[#FF5E1A]/10 via-transparent to-[#007AFF]/10 border-b border-[#27272A]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#FF5E1A]" />
              <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A]">Contribute & Earn</span>
            </div>
            <h3 className="font-['Outfit'] text-xl sm:text-2xl font-black text-white tracking-tight mb-1">
              Grow the tuning internet.
            </h3>
            <p className="text-sm text-zinc-400 max-w-lg">
              Upload raw ECU definitions (Damos, A2L, XDF, PRG). Earn <span className="text-[#FF5E1A] font-bold">10 credits</span> per file + a contributor badge. Your uploads enrich the hybrid database for every future tuner.
            </p>
          </div>
          {data?.badge && <BadgeChip code={data.badge} />}
        </div>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="p-6 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />Loading…
        </div>
      ) : (
        <>
          {data && (
            <div className="grid grid-cols-3 border-b border-[#27272A] text-center" data-testid="contribute-stats">
              <div className="p-4 border-r border-[#27272A]">
                <div className="text-2xl font-['Outfit'] font-black text-white">{data.total_contributions}</div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mt-1">Contributions</div>
              </div>
              <div className="p-4 border-r border-[#27272A]">
                <div className="text-2xl font-['Outfit'] font-black text-[#FF5E1A]">+{data.total_credits_earned}</div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mt-1">Credits earned</div>
              </div>
              <div className="p-4">
                {data.next_badge ? (
                  <>
                    <div className="text-2xl font-['Outfit'] font-black text-[#007AFF]">{data.next_badge.remaining}</div>
                    <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mt-1">Until {BADGE_STYLE[data.next_badge.badge]?.label}</div>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-['Outfit'] font-black text-yellow-300 flex items-center justify-center gap-1">
                      <Sparkles className="w-5 h-5" />MAX
                    </div>
                    <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mt-1">Top tier</div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Upload form */}
          <div className="p-6">
            <UploadForm token={token} onDone={refresh} />
          </div>

          {/* History */}
          {data?.contributions?.length > 0 && (
            <div className="border-t border-[#27272A]">
              <div className="px-6 py-3 flex items-center gap-2 bg-[#0A0A0A]">
                <TrendingUp className="w-4 h-4 text-zinc-500" />
                <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-500">Your contributions</span>
              </div>
              <div className="divide-y divide-[#18181B] max-h-72 overflow-y-auto">
                {data.contributions.map((c) => (
                  <div key={c.id} className="px-6 py-3 flex items-center justify-between gap-3 text-sm hover:bg-[#141414] transition-colors" data-testid={`contribution-row-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                        <span className="text-white font-medium truncate">{c.original_name}</span>
                        <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider px-1.5 py-0.5 bg-[#27272A] text-zinc-400 rounded-sm">{c.file_type}</span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5 truncate">
                        {c.ecu_type}{c.vehicle_make ? ` • ${c.vehicle_make}` : ''}{c.vehicle_model ? ` ${c.vehicle_model}` : ''}{c.year ? ` (${c.year})` : ''}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-emerald-400 font-bold">+{c.credits_awarded}</div>
                      <div className="text-[10px] text-zinc-600 font-['JetBrains_Mono'] uppercase">{c.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
